import {
  Form,
  Modal,
  Select,
  InputNumber,
  notification,
  Divider,
  Typography,
  Statistic,
  Alert,
} from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import {
  addStudents,
  getSupportCourse,
} from "store/slices/supportCoursesSlice";
import { getStudents } from "store/slices/studentsSlice";
import { useTranslation } from "react-i18next";
import {
  UserOutlined,
  CalendarOutlined,
  CalculatorOutlined,
  WalletOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const AddStudentsModal = () => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();
  const dispatch = useDispatch();
  const [learningType, setLearningType] = useState("particulier");
  const [numberOfSessions, setNumberOfSessions] = useState(1);
  const [learningState, setLearningState] = useState("atStudent");
  const [calculatedAmount, setCalculatedAmount] = useState(0);

  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { students } = useSelector((state) => state?.students);
  const { supportCourse, loading } = useSelector(
    (state) => state?.supportCourses
  );

  const open = isModalOpen && modalType === "add_student_to_support_course";
  const supportCourseId = modalProps?.supportCourseId;

  // Calculer le montant en fonction du type d'apprentissage et du nombre de séances
  const calculateAmount = (type, sessions) => {
    console?.log("Support Course:", supportCourse);
    if (!supportCourse?.price) return 0;

    // Utiliser le prix individuel ou standard selon le type d'apprentissage
    const basePrice =
      supportCourse?.price[type] || 0

    return basePrice * sessions;
  };

  // Mettre à jour le montant calculé lors des changements
  useEffect(() => {
    setCalculatedAmount(calculateAmount(learningType, numberOfSessions));
  }, [learningType, numberOfSessions, supportCourse]);

  // Mettre à jour automatiquement le type d'apprentissage si le lieu d'apprentissage change
  useEffect(() => {
    if (learningState === "atStudent" || learningState === "atTeacher") {
      form?.setFieldsValue({ learningType: "particulier" });
      setLearningType("particulier");
    }
  }, [learningState, form]);

  useEffect(() => {
    if (open) {
      dispatch(getStudents());
      // Initialiser le formulaire avec des valeurs par défaut
      form?.setFieldsValue({
        learningType: "particulier",
        learningState: "atStudent",
        numberOfSessions: 1,
      });

      setLearningType("particulier");
      setLearningState("atStudent");
      setNumberOfSessions(1);
    }
  }, [dispatch, open, form]);

  const onSubmit = async (values) => {
    try {
      await dispatch(
        addStudents({
          id: supportCourseId,
          students: [
            {
              studentId: values?.studentId,
              numberOfSessions: values?.numberOfSessions || 1,
              learningState: values?.learningState || "atStudent",
              learningType: values?.learningType || "particulier",
              // Le paiement est calculé côté serveur, pas besoin de l'envoyer
            },
          ],
        })
      )?.unwrap();

      await dispatch(getSupportCourse(supportCourseId));

      notification?.success({
        message: t("supportCourse.student.add.success"),
      });
      onClose();
    } catch (error) {
      notification?.error({
        message: t("supportCourse.student.add.error"),
        description: error || t("common.error.generic"),
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  // Vérifier les états disponibles dans le cours de soutien
  const hasParticulierState = supportCourse?.state?.includes("particulier");
  const hasMiniGroupeState = supportCourse?.state?.includes("miniGroupe");
  const hasGroupState = supportCourse?.state?.includes("group");

  // Filtrer les étudiants déjà inscrits
  const availableStudents = students?.filter(
    (student) =>
      !supportCourse?.students?.some(
        (existingStudent) => existingStudent?.studentId?._id === student?._id
      )
  );

  // Déterminer si le type d'apprentissage doit être désactivé
  const isLearningTypeDisabled =
    learningState === "atStudent" || learningState === "atTeacher";

  return (
    <Modal
      title={t("supportCourse.student.add.title")}
      open={open}
      onCancel={onClose}
      onOk={() => form?.submit()}
      confirmLoading={loading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        onValuesChange={(changedValues) => {
          if (changedValues?.learningType) {
            setLearningType(changedValues?.learningType);
          }
          if (changedValues?.numberOfSessions) {
            setNumberOfSessions(changedValues?.numberOfSessions);
          }
          if (changedValues?.learningState) {
            setLearningState(changedValues?.learningState);
          }
        }}
      >
        <Form.Item
          name="studentId"
          label={t("supportCourse.student.add.form.student")}
          rules={[
            {
              required: true,
              message: t(
                "supportCourse.student.add.form.error.student.required"
              ),
            },
          ]}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase()?.includes(input?.toLowerCase())
            }
            placeholder={t(
              "supportCourse.student.add.form.student.placeholder"
            )}
            notFoundContent={t("common.serch.noResults")}
            suffixIcon={<UserOutlined />}
          >
            {availableStudents?.map((student) => (
              <Select.Option key={student?._id} value={student?._id}>
                {student?.fullName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="numberOfSessions"
          label={t("supportCourse.student.add.form.numberOfSessions")}
          rules={[
            {
              required: true,
              message: t(
                "supportCourse.student.add.form.error.numberOfSessions.required"
              ),
            },
          ]}
          initialValue={1}
        >
          <InputNumber
            min={1}
            style={{ width: "100%" }}
            addonBefore={<CalendarOutlined />}
            addonAfter={t("supportCourse.student.add.form.sessions")}
            placeholder={t(
              "supportCourse.student.add.form.numberOfSessions.placeholder"
            )}
          />
        </Form.Item>

        <Form.Item
          name="learningState"
          label={t("supportCourse.student.add.form.learningState")}
          rules={[
            {
              required: true,
              message: t(
                "supportCourse.student.add.form.error.learningState.required"
              ),
            },
          ]}
          initialValue="atStudent"
          help={
            learningState === "atStudent" || learningState === "atTeacher"
              ? t("supportCourse.student.add.form.learningState.particularHelp")
              : null
          }
        >
          <Select
            placeholder={t(
              "supportCourse.student.add.form.learningState.placeholder"
            )}
          >
            <Select.Option value="atStudent">
              {t("supportCourse.student.learningState.atStudent")}
            </Select.Option>
            <Select.Option value="atSchool">
              {t("supportCourse.student.learningState.atSchool")}
            </Select.Option>
            <Select.Option value="online">
              {t("supportCourse.student.learningState.online")}
            </Select.Option>
            <Select.Option value="atTeacher">
              {t("supportCourse.student.learningState.atTeacher")}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="learningType"
          label={t("students.form.label.learningType")}
          rules={[
            {
              required: true,
              message: t(
                "students.crud.form.error.label.required.learningType"
              ),
            },
          ]}
        >
          <Select
            defaultValue="particulier"
            placeholder={t("students.form.label.learningType.placeholder")}
            disabled={isLearningTypeDisabled}
          >
            {(hasParticulierState || isLearningTypeDisabled) && (
              <Select.Option value="particulier">
                {t("students.form.option.particulier")}
              </Select.Option>
            )}
            {!isLearningTypeDisabled && hasMiniGroupeState && (
              <Select.Option value="miniGroupe">
                {t("students.form.option.miniGroupe")}
              </Select.Option>
            )}
            {!isLearningTypeDisabled && hasGroupState && (
              <Select.Option value="groupe">
                {t("students.form.option.groupe")}
              </Select.Option>
            )}
            {/* Afficher toutes les options si aucun état n'est disponible et pas de restriction */}
            {!isLearningTypeDisabled &&
              !hasParticulierState &&
              !hasMiniGroupeState &&
              !hasGroupState && (
                <>
                  <Select.Option value="particulier">
                    {t("students.form.option.particulier")}
                  </Select.Option>
                  <Select.Option value="miniGroupe">
                    {t("students.form.option.miniGroupe")}
                  </Select.Option>
                  <Select.Option value="groupe">
                    {t("students.form.option.groupe")}
                  </Select.Option>
                </>
              )}
          </Select>
        </Form.Item>

        <Divider orientation="left" plain>
          {t("supportCourse.student.add.form.calculatedAmount")}
        </Divider>
        {calculatedAmount > 0 ? (
          <Statistic
            title={t("supportCourse.student.add.form.amount.dzd")}
            value={calculatedAmount}
            precision={0}
            prefix={<WalletOutlined />}
            suffix="DZD"
            valueStyle={{ color: "#1890ff" }}
          />
        ) : (
          <Alert
            message={t("supportCourse.student.add.form.noPrices")}
            description={t("supportCourse.student.add.form.noPrices.desc")}
            type="warning"
            showIcon
          />
        )}
        <Alert
          message={t("supportCourse.student.add.form.autoCalculation")}
          description={t("supportCourse.student.add.form.autoCalculation.desc")}
          type="info"
          showIcon
          icon={<CalculatorOutlined />}
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default AddStudentsModal;
